import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { Check, Calendar, Users, Clock, Loader2, AlertTriangle } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

function eur(v) {
  return Number(v || 0).toFixed(2).replace('.', ',') + ' €';
}
function qtyStr(q) {
  const n = Number(q) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
}
function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return d; }
}

export default function AcceptOffer() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    api.get(`/reservation-offers/by-token/${token}`)
      .then((res) => {
        setOffer(res.data);
        if (res.data?.status === 'accepted') setAccepted(true);
      })
      .catch(() => setError(true));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/reservation-offers/by-token/${token}/accept`);
      setAccepted(true);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7] px-4" data-testid="accept-offer-error">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-[#B45309] mx-auto mb-3" />
          <p className="text-[#5C5C59]">Dieses Angebot konnte nicht geladen werden oder ist nicht mehr gültig.</p>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="w-10 h-10 border-2 border-[#1C1C19] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-10 px-4" data-testid="accept-offer-page">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <BrandLogo className="h-14 mx-auto mb-3" />
          <p className="text-xs uppercase tracking-[3px] text-[#5C5C59]">Angebot</p>
        </div>

        <div className="bg-white border border-[#E5E5E0] rounded-xl p-6 shadow-sm">
          {accepted && (
            <div className="mb-5 flex items-center gap-3 p-4 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0]" data-testid="accept-offer-success">
              <div className="w-9 h-9 rounded-full bg-[#16A34A] flex items-center justify-center shrink-0">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-[#166534]">Vielen Dank! Ihr Angebot ist angenommen.</p>
                <p className="text-sm text-[#166534]/80">Wir haben Ihre Zusage erhalten und melden uns bei Ihnen.</p>
              </div>
            </div>
          )}

          <h1 className="font-display text-2xl text-[#1C1C19] mb-1">
            Guten Tag{offer.customer_name ? ` ${offer.customer_name}` : ''},
          </h1>
          <p className="text-sm text-[#5C5C59] mb-5">
            hier ist Ihr persönliches Angebot von <strong>{offer.restaurant_name}</strong>.
          </p>

          <div className="space-y-1.5 text-sm text-[#1C1C19] mb-5 pb-5 border-b border-[#F1F1EE]">
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#9CA3AF]" /> {fmtDate(offer.date)}</div>
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#9CA3AF]" /> {offer.start_time}{offer.end_time ? ` – ${offer.end_time}` : ''}</div>
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#9CA3AF]" /> {offer.persons} Personen</div>
          </div>

          <div className="space-y-3 mb-5" data-testid="accept-offer-items">
            {(offer.items || []).map((it, i) => {
              const lt = it.line_total != null ? it.line_total : (Number(it.quantity || 0) * Number(it.unit_price || 0));
              return (
                <div key={i} className="flex justify-between gap-4 text-sm">
                  <div>
                    <div className="font-medium text-[#1C1C19]">{it.description}</div>
                    <div className="text-xs text-[#9CA3AF]">{qtyStr(it.quantity)} × {eur(it.unit_price)}</div>
                  </div>
                  <div className="font-semibold text-[#1C1C19] whitespace-nowrap">{eur(lt)}</div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center pt-4 border-t-2 border-[#1C1C19] mb-2">
            <span className="font-semibold text-[#1C1C19]">Gesamt</span>
            <span className="text-xl font-bold text-[#1C1C19]" data-testid="accept-offer-total">{eur(offer.total)}</span>
          </div>

          {offer.note && (
            <p className="text-sm text-[#5C5C59] whitespace-pre-line mt-4 p-3 bg-[#F9F9F7] rounded-lg">{offer.note}</p>
          )}

          {!accepted && (
            <button
              onClick={accept}
              disabled={busy}
              data-testid="accept-offer-btn"
              className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold py-3.5 rounded-lg transition disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              Angebot annehmen
            </button>
          )}
          <p className="text-xs text-[#9CA3AF] text-center mt-3">
            Dieses Angebot ist freibleibend. Bei Fragen antworten Sie einfach auf unsere E-Mail.
          </p>
        </div>
      </div>
    </div>
  );
}
